import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
    ChevronDown,
    ChevronRight,
    Circle,
    PlayCircle
} from 'lucide-react'
import { contentApi } from '@/api/content'
import classNames from 'classnames'

interface CourseContentProps {
    courseId: number
    currentLessonId?: number
    className?: string
}

export function CourseContent({ courseId, currentLessonId, className }: CourseContentProps) {
    const [openModules, setOpenModules] = useState<Record<number, boolean>>({})

    const { data: course, isLoading } = useQuery({
        queryKey: ['course', courseId],
        queryFn: () => contentApi.getCourseDetail(courseId),
        enabled: !!courseId,
        staleTime: 10 * 60 * 1000,
    })

    // Automatically expand the module containing the current lesson
    useEffect(() => {
        if (course && currentLessonId) {
            const currentModule = course.modules.find(m =>
                m.lessons.some(l => l.id === currentLessonId)
            )

            if (currentModule) {
                setOpenModules(prev => ({
                    ...prev,
                    [currentModule.id]: true
                }))
            }
        }
    }, [course, currentLessonId])

    const toggleModule = (moduleId: number) => {
        setOpenModules(prev => ({
            ...prev,
            [moduleId]: !prev[moduleId]
        }))
    }

    if (isLoading) {
        return (
            <div className="space-y-4 p-4">
                {[1, 2].map(i => (
                    <div key={i} className="space-y-2">
                        <div className="h-8 bg-gray-100 rounded-sm animate-pulse" />
                        <div className="h-6 bg-gray-100 rounded-sm w-1/2 ml-4 animate-pulse" />
                    </div>
                ))}
            </div>
        )
    }

    if (!course) return null

    return (
        <div className={classNames("bg-gray-50/50", className)}>
            {course.modules.map((module) => {
                const isOpen = openModules[module.id]
                // const hasCurrentLesson = currentLessonId ? module.lessons.some(l => l.id === currentLessonId) : false

                return (
                    <div key={module.id} className="border-b border-gray-100 last:border-0">
                        {/* Module Header */}
                        <button
                            onClick={() => toggleModule(module.id)}
                            className={classNames(
                                "w-full flex items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-gray-100",
                                // { "bg-gray-100": isOpen }
                            )}
                        >
                            <div className="shrink-0 text-gray-400">
                                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-700 truncate">
                                    {module.title}
                                </div>
                            </div>
                        </button>

                        {/* Lessons List */}
                        {isOpen && (
                            <div className="bg-white">
                                {module.lessons.map((lesson) => {
                                    const isCurrent = lesson.id === currentLessonId

                                    return (
                                        <Link
                                            key={lesson.id}
                                            to={`/learn/lesson/${lesson.id}`}
                                            className={classNames(
                                                "group flex items-center gap-3 pl-9 pr-4 py-2 transition-all hover:bg-gray-50 relative",
                                                {
                                                    "bg-indigo-50/60 hover:bg-indigo-50 text-indigo-900 border-r-2 border-indigo-500": isCurrent,
                                                    "text-gray-600": !isCurrent
                                                }
                                            )}
                                        >
                                            <div className={classNames("shrink-0", {
                                                "text-indigo-600": isCurrent,
                                                "text-gray-300 group-hover:text-gray-400": !isCurrent
                                            })}>
                                                {isCurrent ? (
                                                    <PlayCircle className="w-3.5 h-3.5 fill-indigo-100" />
                                                ) : (
                                                    <Circle className="w-2 h-2" />
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className={classNames("text-sm truncate transition-colors", {
                                                    "font-medium text-indigo-700": isCurrent,
                                                    "text-gray-600 group-hover:text-gray-900": !isCurrent
                                                })}>
                                                    {lesson.title}
                                                </div>
                                            </div>
                                        </Link>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
